import React from "react";
import { useForm } from "react-hook-form";
import { CreditCard, Building } from "lucide-react";

interface BillingFormData {
  paymentMethod: {
    cardNumber: string;
    expiryMonth: string;
    expiryYear: string;
    cvc: string;
    name: string;
  };
  billingAddress: {
    line1: string;
    line2?: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  billingEmail: string;
  autoRenew: boolean;
}

interface BillingStepProps {
  onNext: (data: BillingFormData) => void;
  onBack: () => void;
  initialData?: BillingFormData;
}

export const BillingStep: React.FC<BillingStepProps> = ({
  onNext,
  onBack,
  initialData,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<BillingFormData>({
    defaultValues: initialData,
  });

  return (
    <form onSubmit={handleSubmit(onNext)} className="space-y-8">
      {/* Payment Method Section */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Payment Method
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Card Number
            </label>
            <div className="mt-1 relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
                <CreditCard className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                {...register("paymentMethod.cardNumber", {
                  required: "Card number is required",
                  pattern: {
                    value: /^[0-9]{16}$/,
                    message: "Please enter a valid card number",
                  },
                })}
                placeholder="1234 5678 9012 3456"
                className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:ring-purple-500 focus:border-purple-500"
              />
            </div>
            {errors.paymentMethod?.cardNumber && (
              <p className="mt-1 text-sm text-red-600">
                {errors.paymentMethod.cardNumber.message}
              </p>
            )}
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700">
                Expiration Date
              </label>
              <div className="mt-1 grid grid-cols-2 gap-2">
                <select
                  {...register("paymentMethod.expiryMonth", {
                    required: "Required",
                  })}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value="">Month</option>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                    <option
                      key={month}
                      value={month.toString().padStart(2, "0")}
                    >
                      {month.toString().padStart(2, "0")}
                    </option>
                  ))}
                </select>
                <select
                  {...register("paymentMethod.expiryYear", {
                    required: "Required",
                  })}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value="">Year</option>
                  {Array.from(
                    { length: 10 },
                    (_, i) => new Date().getFullYear() + i
                  ).map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                CVC
              </label>
              <input
                type="text"
                {...register("paymentMethod.cvc", {
                  required: "CVC is required",
                  pattern: {
                    value: /^[0-9]{3,4}$/,
                    message: "Invalid CVC",
                  },
                })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-purple-500 focus:border-purple-500"
                placeholder="123"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Cardholder Name
            </label>
            <input
              type="text"
              {...register("paymentMethod.name", {
                required: "Cardholder name is required",
              })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-purple-500 focus:border-purple-500"
              placeholder="John Doe"
            />
          </div>
        </div>
      </div>

      {/* Billing Address Section */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Billing Address
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Street Address
            </label>
            <div className="mt-1 relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
                <Building className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                {...register("billingAddress.line1", {
                  required: "Street address is required",
                })}
                className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:ring-purple-500 focus:border-purple-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Apartment, suite, etc. (optional)
            </label>
            <input
              type="text"
              {...register("billingAddress.line2")}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-purple-500 focus:border-purple-500"
            />
          </div>

          <div className="grid grid-cols-6 gap-4">
            <div className="col-span-3">
              <label className="block text-sm font-medium text-gray-700">
                City
              </label>
              <input
                type="text"
                {...register("billingAddress.city", {
                  required: "City is required",
                })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-purple-500 focus:border-purple-500"
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700">
                State
              </label>
              <select
                {...register("billingAddress.state", {
                  required: "State is required",
                })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-purple-500 focus:border-purple-500"
              >
                <option value="">Select state</option>
                <option value="CA">California</option>
                <option value="NY">New York</option>
                <option value="TX">Texas</option>
                {/* Add more states */}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                ZIP
              </label>
              <input
                type="text"
                {...register("billingAddress.zipCode", {
                  required: "ZIP code is required",
                  pattern: {
                    value: /^\d{5}(-\d{4})?$/,
                    message: "Invalid ZIP code",
                  },
                })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-purple-500 focus:border-purple-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Billing Email & Preferences */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Billing Preferences
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Billing Email
            </label>
            <input
              type="email"
              {...register("billingEmail", {
                required: "Billing email is required",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "Invalid email address",
                },
              })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-purple-500 focus:border-purple-500"
            />
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              {...register("autoRenew")}
              className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
            />
            <label className="ml-2 block text-sm text-gray-700">
              Automatically renew my subscription
            </label>
          </div>
        </div>
      </div>

      <div className="flex justify-between pt-6">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
        >
          Back
        </button>
        <button
          type="submit"
          className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
        >
          Next Step
        </button>
      </div>
    </form>
  );
};
